import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bot, MessageSquare, RefreshCw, Send, Smartphone, Sparkles } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  createOrGetSupportConversation,
  fetchConversationMessages,
  generateConversationAIDraft,
  hasCommunicationMedia,
  improveConversationAIMessage,
  sendConversationMessage,
  sendDirectSupportWhatsAppMessage,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import MessageBubble from './MessageBubble';
import MediaMessageControls from './MediaMessageControls';

const TARGET_LABELS = {
  driver: 'Motorizado',
  store: 'Tienda',
  provider: 'Proveedor',
};

function deliveryPrefix(delivery) {
  const id = delivery?.id || delivery?.current_delivery_id;
  return id ? `Entrega #${id}:` : 'Entrega:';
}

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.error || fallback;
}

export default function DirectParticipantChatCard({
  targets = [],
  delivery,
  currentUserId,
}) {
  const availableTargets = useMemo(
    () => (targets || []).filter(target => target?.user_id && target?.scope),
    [targets]
  );
  const [localTargets, setLocalTargets] = useState(availableTargets);
  const [selectedType, setSelectedType] = useState(availableTargets[0]?.target_type || null);
  const [supportConversationId, setSupportConversationId] = useState(availableTargets[0]?.support_conversation_id || null);
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState('internal');
  const [draft, setDraft] = useState('');
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [aiDraftMeta, setAiDraftMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAIWorking, setIsAIWorking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLocalTargets(availableTargets);
    setSelectedType(prev => (
      availableTargets.some(target => target.target_type === prev)
        ? prev
        : availableTargets[0]?.target_type || null
    ));
    setAiDraftMeta(null);
  }, [availableTargets]);

  const selectedTarget = useMemo(
    () => localTargets.find(target => target.target_type === selectedType) || localTargets[0] || null,
    [localTargets, selectedType]
  );
  const originDeliveryPointId = delivery?.id || delivery?.current_delivery_id || null;

  const aiRequestPayload = useCallback((target = selectedTarget) => ({
    preview_only: true,
    source: 'direct_participant_mini_chat',
    delivery_point_id: originDeliveryPointId,
    target_type: target?.target_type,
  }), [originDeliveryPointId, selectedTarget]);

  const ensureSupportConversation = useCallback(async (target = selectedTarget) => {
    if (!target?.scope || !target?.user_id) return null;
    if (target.support_conversation_id) return target.support_conversation_id;

    const conversation = await createOrGetSupportConversation(target.scope, target.user_id);
    const nextId = conversation?.id || null;
    if (nextId) {
      setLocalTargets(prev => prev.map(item => (
        item.target_type === target.target_type
          ? { ...item, support_conversation_id: nextId }
          : item
      )));
    }
    return nextId;
  }, [selectedTarget]);

  const loadMessages = useCallback(async () => {
    if (!selectedTarget) return;
    setIsLoading(true);
    setError(null);
    try {
      const conversationId = await ensureSupportConversation(selectedTarget);
      setSupportConversationId(conversationId);
      if (!conversationId) {
        setMessages([]);
        return;
      }
      const data = await fetchConversationMessages(conversationId, {
        currentUserType: 'admin',
        currentUserId,
      });
      setMessages((data.messages || []).slice(-6));
    } catch (err) {
      setError(compactError(err, 'No se pudo cargar el chat directo.'));
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, ensureSupportConversation, selectedTarget]);

  useEffect(() => {
    setMessages([]);
    setSupportConversationId(selectedTarget?.support_conversation_id || null);
    if (selectedTarget) loadMessages();
  }, [selectedTarget?.target_type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!supportConversationId) return undefined;
    const payload = socketAuthPayload({ conversation_id: supportConversationId });
    const join = () => socket.emit('join_conversation', payload);
    const handleMessage = (eventPayload) => {
      const message = eventPayload?.message || eventPayload;
      if (Number(message?.conversation_id) !== Number(supportConversationId)) return;
      setMessages(prev => {
        if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
        return [...prev, message].slice(-6);
      });
    };
    const refresh = (eventPayload = null) => {
      if (eventPayload?.conversation_id && Number(eventPayload.conversation_id) !== Number(supportConversationId)) return;
      loadMessages();
    };
    const rejoinAndRefresh = () => {
      join();
      loadMessages();
    };

    join();
    socket.on('connect', rejoinAndRefresh);
    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_action_completed', refresh);
    return () => {
      socket.off('connect', rejoinAndRefresh);
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_action_completed', refresh);
      socket.emit('leave_conversation', payload);
    };
  }, [loadMessages, supportConversationId]);

  async function handleSend(event) {
    event.preventDefault();
    const text = draft.trim();
    if ((!text && !mediaAttachment) || !selectedTarget || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const conversationId = await ensureSupportConversation(selectedTarget);
      setSupportConversationId(conversationId);
      if (!conversationId) throw new Error('missing_support_conversation');

      const content = `${deliveryPrefix(delivery)} ${text}`.trim();
      const payload = {
        content,
        message_type: mediaAttachment?.type || 'text',
        media_file: mediaAttachment?.file,
        media_type: mediaAttachment?.type,
      };
      if (channel === 'whatsapp') {
        if (!selectedTarget.whatsapp_available) {
          throw new Error(selectedTarget.whatsapp_disabled_reason || 'WhatsApp no disponible.');
        }
        await sendDirectSupportWhatsAppMessage(conversationId, hasCommunicationMedia(payload) ? buildCommunicationMessageFormData({
          ...payload,
          delivery_point_id: delivery?.id || delivery?.current_delivery_id,
          target_type: selectedTarget.target_type,
        }) : {
          content,
          delivery_point_id: delivery?.id || delivery?.current_delivery_id,
          target_type: selectedTarget.target_type,
        });
      } else {
        await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
      }
      setDraft('');
      setMediaAttachment(null);
      setAiDraftMeta(null);
      await loadMessages();
    } catch (err) {
      setError(compactError(err, err.message || 'No se pudo enviar el mensaje directo.'));
    } finally {
      setIsSending(false);
    }
  }

  async function handleGenerateAIDraft() {
    if (!selectedTarget || isAIWorking) return;
    setIsAIWorking(true);
    setError(null);
    try {
      const conversationId = await ensureSupportConversation(selectedTarget);
      setSupportConversationId(conversationId);
      if (!conversationId) throw new Error('missing_support_conversation');

      const result = await generateConversationAIDraft(
        conversationId,
        aiRequestPayload(selectedTarget)
      );
      const nextDraft = result?.draft;
      const nextText = (nextDraft?.draft_text || '').trim();
      if (!nextText) throw new Error('AI no devolvio un borrador utilizable.');

      setDraft(nextText);
      setAiDraftMeta(nextDraft);
      setChannel(selectedTarget.whatsapp_available ? 'whatsapp' : 'internal');
    } catch (err) {
      setError(compactError(err, err.message || 'No se pudo generar el borrador AI.'));
    } finally {
      setIsAIWorking(false);
    }
  }

  async function handleImproveDraft() {
    const text = draft.trim();
    if (!text || !selectedTarget || isAIWorking) return;
    setIsAIWorking(true);
    setError(null);
    try {
      const conversationId = await ensureSupportConversation(selectedTarget);
      setSupportConversationId(conversationId);
      if (!conversationId) throw new Error('missing_support_conversation');

      const result = await improveConversationAIMessage(
        conversationId,
        text,
        aiRequestPayload(selectedTarget)
      );
      setDraft(result.content || text);
      setAiDraftMeta(null);
    } catch (err) {
      setError(compactError(err, err.message || 'No se pudo corregir el texto.'));
    } finally {
      setIsAIWorking(false);
    }
  }

  if (!availableTargets.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-extrabold text-slate-900">Direct participant chat</h3>
        <p className="mt-1 text-xs text-slate-500">No hay motorizado, tienda o proveedor vinculado para chat directo.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-extrabold text-slate-900">Direct participant chat</h3>
          <p className="text-xs text-slate-500">Mensaje directo con contexto de esta entrega</p>
        </div>
        <button onClick={loadMessages} disabled={isLoading} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {localTargets.map(target => (
          <button
            key={target.target_type}
            type="button"
            onClick={() => {
              setSelectedType(target.target_type);
              setChannel('internal');
              setAiDraftMeta(null);
              setMediaAttachment(null);
              setError(null);
            }}
            className={`rounded-lg px-2.5 py-1.5 text-[11px] font-extrabold ${
              selectedTarget?.target_type === target.target_type
                ? 'bg-[#B1336E] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {TARGET_LABELS[target.target_type] || target.target_type}
          </button>
        ))}
      </div>

      {selectedTarget && (
        <>
          <div className="mb-2 rounded-lg bg-slate-50 p-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-extrabold text-slate-900">{selectedTarget.label}</span>
              <span className="text-slate-400">{selectedTarget.phone_number || 'sin WhatsApp'}</span>
            </div>
            {selectedTarget.whatsapp_disabled_reason && (
              <p className="mt-1 text-[11px] font-semibold text-amber-700">{selectedTarget.whatsapp_disabled_reason}</p>
            )}
          </div>

          <div className="mb-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setChannel('internal')}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold ${
                channel === 'internal' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Interno
            </button>
            <button
              type="button"
              onClick={() => selectedTarget.whatsapp_available && setChannel('whatsapp')}
              disabled={!selectedTarget.whatsapp_available}
              className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
                channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" />
              WhatsApp
            </button>
          </div>

          <div className="mb-3 max-h-52 space-y-2 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-2">
            {!messages.length && !isLoading && (
              <div className="rounded-lg border border-dashed border-slate-200 bg-white p-3 text-center text-xs text-slate-500">
                Sin mensajes directos recientes.
              </div>
            )}
            {messages.map(message => (
              <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType="admin" />
            ))}
          </div>

          <form onSubmit={handleSend} className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleGenerateAIDraft}
                disabled={isAIWorking || isSending || !selectedTarget}
                className="inline-flex items-center gap-1.5 rounded-lg bg-purple-100 px-2.5 py-1.5 text-[11px] font-extrabold text-purple-700 hover:bg-purple-200 disabled:opacity-50"
              >
                <Bot className={`h-3.5 w-3.5 ${isAIWorking ? 'animate-pulse' : ''}`} />
                AI draft
              </button>
              <button
                type="button"
                onClick={handleImproveDraft}
                disabled={isAIWorking || isSending || !draft.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-2.5 py-1.5 text-[11px] font-extrabold text-sky-700 hover:bg-sky-200 disabled:opacity-50"
              >
                <Sparkles className={`h-3.5 w-3.5 ${isAIWorking ? 'animate-pulse' : ''}`} />
                Corregir
              </button>
              {aiDraftMeta && (
                <span className="rounded-full bg-purple-50 px-2 py-1 text-[10px] font-bold text-purple-700">
                  {aiDraftMeta.intent || 'draft'} {aiDraftMeta.confidence != null ? `- ${Math.round((aiDraftMeta.confidence || 0) * 100)}%` : ''}
                </span>
              )}
            </div>
            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              disabled={isSending || isAIWorking}
              rows={2}
              placeholder={`Mensaje para ${TARGET_LABELS[selectedTarget.target_type] || selectedTarget.target_type}...`}
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
            />
            <MediaMessageControls
              value={mediaAttachment}
              onChange={setMediaAttachment}
              disabled={isSending || isAIWorking}
              compact
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-slate-500">
                Se enviara como: {deliveryPrefix(delivery)} ...
              </span>
              <button
                type="submit"
                disabled={isSending || isAIWorking || (!draft.trim() && !mediaAttachment) || (channel === 'whatsapp' && !selectedTarget.whatsapp_available)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                Enviar
              </button>
            </div>
          </form>
        </>
      )}

      {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">{error}</div>}
    </div>
  );
}
